import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HeightInput } from './height-input';
import { WeightInput } from './weight-input';

interface ScrollComponentProps {
  value: number;
  onChange: (value: number) => void;
  type: 'weight' | 'height';
  unit: 'lb' | 'kg' | 'ft' | 'cm';
  min?: number;
  max?: number;
  step?: number;
}

interface ScaleConfig {
  min: number;
  max: number;
  step: number;
  majorStep: number;
  unit: string;
}

const SCALE_CONFIGS: Record<string, ScaleConfig> = {
  'weight-lb': { min: 50, max: 500, step: 1, majorStep: 10, unit: 'lb' },
  'weight-kg': { min: 20, max: 250, step: 2, majorStep: 10, unit: 'kg' },
  'height-ft': { min: 2, max: 8, step: 1 / 12, majorStep: 1, unit: 'ft' },
  'height-cm': { min: 120, max: 250, step: 1, majorStep: 10, unit: 'cm' },
};

// Convert decimal feet to digit string format for HeightInput
const convertFeetToDigitString = (decimalFeet: number): string => {
  const feet = Math.floor(decimalFeet);
  const inches = Math.round((decimalFeet - feet) * 12);

  // Handle edge case where rounding gives us 12 inches
  if (inches === 12) {
    return `${feet + 1}0`;
  }

  return `${feet}${inches}`;
};

// Convert digit string format back to decimal feet
const convertDigitStringToFeet = (digitString: string): number => {
  if (digitString.length === 0) return 0;
  if (digitString.length === 1) return parseInt(digitString);

  const feet = parseInt(digitString.slice(0, -1)) || 0;
  const inches = parseInt(digitString.slice(-1)) || 0;

  return feet + inches / 12;
};

export function ScrollComponent({ value, onChange, type, unit, min, max, step }: ScrollComponentProps) {
  const [inputValue, setInputValue] = useState(() => {
    if (type === 'height' && unit === 'ft') {
      return convertFeetToDigitString(value);
    }
    return value.toString();
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; value: number } | null>(null);
  const scaleRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const lastValueRef = useRef<number>(value);
  const pointerIdRef = useRef<number | null>(null);

  const configKey = `${type}-${unit}`;
  const config = SCALE_CONFIGS[configKey] || SCALE_CONFIGS['weight-lb'];

  const actualMin = min ?? config.min;
  const actualMax = max ?? config.max;
  const actualStep = step ?? config.step;
  const majorStep = config.majorStep;

  useEffect(() => {
    if (type === 'height' && unit === 'ft') {
      setInputValue(convertFeetToDigitString(value));
    } else {
      setInputValue(value.toString());
    }
    lastValueRef.current = value;
  }, [value, type, unit]);

  // taking a mark based approach where each mark is spaced 8px apart
  const getMarkValues = useCallback(() => {
    const containerWidth = scaleRef.current?.getBoundingClientRect().width || 335;
    const markSpacing = 8;
    const maxMarks = Math.floor(containerWidth / markSpacing);

    const centerMarkIndex = Math.floor(maxMarks / 2);
    const currentStep = Math.round(value / actualStep);

    const markValues = [];
    for (let i = 0; i < maxMarks; i++) {
      const stepOffset = i - centerMarkIndex;
      const markStep = currentStep + stepOffset;
      const markValue = markStep * actualStep;

      if (markValue >= actualMin && markValue <= actualMax) {
        markValues.push({
          value: markValue,
          position: i * markSpacing,
        });
      }
    }

    return markValues;
  }, [value, actualStep, actualMin, actualMax]);

  const handleInputChange = (value: string) => {
    setInputValue(value);

    let numValue: number;
    if (type === 'height' && unit === 'ft') {
      numValue = convertDigitStringToFeet(value);
    } else {
      numValue = parseFloat(value);
    }

    if (!isNaN(numValue)) {
      // Clamp the value to be within bounds
      const clampedValue = Math.max(actualMin, Math.min(actualMax, numValue));
      onChange(clampedValue);
    }
  };

  const handleScaleScroll = (e: React.WheelEvent) => {
    const delta = e.deltaX || e.deltaY;
    const direction = delta > 0 ? -1 : 1;

    const newValue = value + direction * actualStep;
    const clampedValue = Math.max(actualMin, Math.min(actualMax, newValue));
    onChange(clampedValue);
  };

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      // Only handle if this is our tracked pointer or we're not dragging
      if (isDragging && pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current) {
        return;
      }

      // Release pointer capture
      if (e.currentTarget && 'releasePointerCapture' in e.currentTarget) {
        try {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch (error) {
          // Ignore errors - pointer might already be released
        }
      }

      // If we haven't moved much, treat it as a click rather than a drag
      if (dragStart) {
        const rect = scaleRef.current?.getBoundingClientRect();
        if (rect) {
          const x = e.clientX - rect.left;
          const dragDistance = Math.abs(x - dragStart.x);

          if (dragDistance < 5) {
            const stepIndex = Math.round((x - 8) / 8);
            const markValues = getMarkValues();

            if (stepIndex >= 0 && stepIndex < markValues.length) {
              const clickedValue = markValues[stepIndex].value;
              const clampedValue = Math.max(actualMin, Math.min(actualMax, clickedValue));

              if (clampedValue !== value) {
                onChange(clampedValue);
              }
            }
          }
        }
      }

      setIsDragging(false);
      setDragStart(null);
      pointerIdRef.current = null;
    },
    [isDragging, dragStart, value, onChange, actualMin, actualMax, getMarkValues],
  );

  // Handle global pointer events during dragging
  useEffect(() => {
    const handleGlobalPointerMove = (e: PointerEvent) => {
      if (!isDragging || !dragStart || !rectRef.current) return;

      const x = e.clientX - rectRef.current.left;
      const dragDistance = -(x - dragStart.x);

      const stepMovement = Math.round(dragDistance / 8);
      const newValue = dragStart.value + stepMovement * actualStep;
      const clampedValue = Math.max(actualMin, Math.min(actualMax, newValue));

      // Only update if the value actually changed to avoid unnecessary renders
      if (clampedValue !== lastValueRef.current) {
        lastValueRef.current = clampedValue;
        onChange(clampedValue);
      }
    };

    const handleGlobalPointerUp = (e: PointerEvent) => {
      const syntheticEvent = {
        clientX: e.clientX,
        currentTarget: e.currentTarget,
        pointerId: e.pointerId,
      } as React.PointerEvent;

      handlePointerUp(syntheticEvent);
    };

    if (isDragging) {
      // Use passive listeners for better performance
      const options = { passive: true };
      document.addEventListener('pointermove', handleGlobalPointerMove, options);
      document.addEventListener('pointerup', handleGlobalPointerUp);
      document.addEventListener('pointercancel', handleGlobalPointerUp);
    }

    return () => {
      document.removeEventListener('pointermove', handleGlobalPointerMove);
      document.removeEventListener('pointerup', handleGlobalPointerUp);
      document.removeEventListener('pointercancel', handleGlobalPointerUp);
    };
  }, [isDragging, dragStart, value, onChange, handlePointerUp, actualStep, actualMin, actualMax]);

  const scaleMarks = useMemo(() => {
    const marks: JSX.Element[] = [];
    const markValues = getMarkValues();

    markValues.forEach((mark, index) => {
      const isMajor = mark.value % majorStep === 0;

      let isMinor = false;
      if (type === 'height') {
        if (unit === 'ft') {
          const inchPosition = (mark.value * 12) % 12;
          isMinor = Math.abs(inchPosition - 6) < 0.01;
        } else if (unit === 'cm') {
          isMinor = mark.value % majorStep === 5;
        }
      } else {
        isMinor = mark.value % majorStep === 5;
      }

      marks.push(
        <div
          key={index}
          className="scroll-scale-mark"
          style={{
            left: `${mark.position}px`,
          }}
        >
          {isMajor && (
            <span className="scroll-scale-mark-label">{mark.value % 1 === 0 ? mark.value : mark.value.toFixed(1)}</span>
          )}
          <div
            className={
              isMajor ? 'scroll-scale-tick-major' : isMinor ? 'scroll-scale-tick-minor' : 'scroll-scale-tick-regular'
            }
          />
        </div>,
      );
    });

    return marks;
  }, [getMarkValues, majorStep, type, unit]);

  // Handle touch and mouse interactions
  const handlePointerDown = (e: React.PointerEvent) => {
    const rect = scaleRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Cache the rect to avoid recalculating during drag
    rectRef.current = rect;

    // Store pointer ID and capture the pointer
    pointerIdRef.current = e.pointerId;
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch (error) {
      console.warn('Failed to capture pointer:', error);
    }

    const x = e.clientX - rect.left;
    setDragStart({ x, value });
    setIsDragging(true);
  };

  // Component JSX with updated indicator positioning
  return (
    <div ref={scaleRef} className="scroll-scale-container">
      <div className="scroll-type-container">
        <span className="scroll-type-text">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
      </div>

      <div className="scroll-input-container">
        {type === 'height' ? (
          <HeightInput
            className="scroll-input"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={`${actualMin} ${unit}`}
          />
        ) : (
          <WeightInput
            className="scroll-input"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={`${actualMin} ${unit}`}
          />
        )}
      </div>

      {/* Scale Marks - Static scale showing visible range */}
      <motion.div
        key={`scale-${Math.floor(value / (5 * actualStep))}`} // Re-render when we move significantly
        className="scroll-scale-marks-container"
        style={{ touchAction: 'none' }}
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.2,
        }}
        onWheel={handleScaleScroll}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={(e) => {
          // Only handle pointer leave if we're not actively dragging
          if (!isDragging) {
            handlePointerUp(e);
          }
        }}
      >
        {scaleMarks}
      </motion.div>

      {/* Current Value Indicator */}
      <motion.div
        className="scroll-indicator"
        initial={false}
        animate={{
          left: (() => {
            const markValues = getMarkValues();
            const currentStep = Math.round(value / actualStep);
            const currentValue = currentStep * actualStep;
            const closestMark = markValues.reduce(
              (closest, mark) =>
                Math.abs(mark.value - currentValue) < Math.abs(closest.value - currentValue) ? mark : closest,
              markValues[0] || { value: currentValue, position: 0 },
            );
            // Mark is 2px wide, so we need to add 7px to the position to center it
            return `${closestMark.position + 7}px`;
          })(),
        }}
        transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300,
          duration: 0.1,
        }}
      />

      {/* Touch/Drag Target */}
      <div className="scale-drag-target" style={{ touchAction: 'none' }} />
    </div>
  );
}
